import { MessageSquare } from "lucide-react";
import { Link } from "react-router-dom";
import { CalendarClock } from "lucide-react";
import { fileUrl } from "@/lib/api";
import { useWorld } from "@/context/WorldContext";
import { OriginBadge } from "@/components/Origin";
import { cardFacts, shipLabel, speciesStatus } from "@/lib/infobox";
import { PairingLine, ShipAvatars, ShipLabelBadge } from "@/components/ShipBits";
import { SpeciesMeta } from "@/components/SpeciesBits";

const SMALL = new Set(["of", "the", "a", "an", "and", "in", "on", "to"]);
const initials = (t) => {
  const words = t.split(/\s+/).filter(Boolean);
  const main = words.filter((w) => !SMALL.has(w.toLowerCase()));
  return (main.length ? main : words).map((w) => w[0]).slice(0, 2).join("").toUpperCase();
};

export const timeAgo = (iso) => {
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  const steps = [[60, "s"], [60, "m"], [24, "h"], [30, "d"], [12, "mo"]];
  let v = s;
  for (const [n, u] of steps) {
    if (v < n) return `${Math.max(1, Math.floor(v))}${u} ago`;
    v /= n;
  }
  return `${Math.floor(v)}y ago`;
};

// Whole image, letterboxed over a blurred copy of itself
const Cover = ({ page, meta }) => (
  <div className="relative w-full h-full overflow-hidden">
    {page.image ? (
      <>
        <img src={fileUrl(page.image)} alt="" aria-hidden className="absolute inset-0 w-full h-full object-cover blur-xl scale-110 opacity-40" />
        <img src={fileUrl(page.image)} alt={page.title} className="relative w-full h-full object-contain transition-transform duration-700 group-hover:scale-[1.03]" data-testid="entry-card-image" />
      </>
    ) : <div className={`w-full h-full flex items-center justify-center font-display text-3xl ${meta.bg} ${meta.text}`}>{initials(page.title)}</div>}
  </div>
);

const Thumb = ({ page, meta, className }) =>
  page.image ? (
    <img src={fileUrl(page.image)} alt="" className={`object-cover ${className}`} />
  ) : (
    <div className={`flex items-center justify-center font-display ${meta.bg} ${meta.text} ${className}`}>{initials(page.title)}</div>
  );

const extinct = (page) => page.category === "Species" && speciesStatus(page) === "Extinct";

const CommentCount = ({ id }) => {
  const n = useWorld().commentCounts?.[id];
  return n ? <span className="absolute top-2 right-2 z-10 inline-flex items-center gap-1 rounded-full bg-black/70 px-2 py-0.5 text-[10px] text-white" data-testid={`card-comments-${id}`}><MessageSquare className="w-3 h-3" />{n}</span> : null;
};

export const EntryCard = ({ page, meta, to, i }) => (
  <Link to={to} data-testid={`entry-card-${page.id}`} data-extinct={extinct(page)} style={{ "--glow": meta.hex, animationDelay: `${Math.min(i, 12) * 45}ms` }}
    className={`rise group block ${extinct(page) ? "opacity-60 grayscale-[60%] hover:opacity-90" : ""} rounded-xl overflow-hidden border border-line bg-panel transition-[border-color,box-shadow] duration-300 hover:border-[color:var(--glow)] hover:shadow-[0_0_28px_-8px_var(--glow)]`}>
    <div className="relative aspect-[4/3] overflow-hidden bg-canvas">
      <CommentCount id={page.id} />
      {meta.key === "Ships" ? (
        <div className="w-full h-full flex items-center justify-center bg-[radial-gradient(circle_at_50%_60%,rgba(236,72,153,0.18),transparent_70%)] transition-transform duration-700 group-hover:scale-105"><ShipAvatars page={page} size="w-12 h-12 text-base" /></div>
      ) : <Cover page={page} meta={meta} />}
    </div>
    <div className="p-3 space-y-1.5">
      {meta.key === "Events" && page.event_date && (
        <p className="flex items-center gap-1 text-xs font-code text-rose-300" data-testid="entry-event-date"><CalendarClock className="w-3 h-3" /> {page.event_date}</p>
      )}
      <h3 className="font-display text-lg sm:text-xl text-amber-50 leading-tight group-hover:text-amber-200 transition-colors line-clamp-2">{page.title} <OriginBadge page={page} /></h3>
      {meta.key === "Species" && <SpeciesMeta page={page} />}
      {meta.key === "Ships" && <div className="flex flex-wrap items-center gap-2"><PairingLine page={page} className="text-xs" /><ShipLabelBadge label={shipLabel(page)} /></div>}
      {page.summary && <p className="text-xs text-slate-400 line-clamp-2">{page.summary}</p>}
      <CardFacts page={page} />
    </div>
  </Link>
);

const CardFacts = ({ page }) => {
  const { pages } = useWorld();
  const facts = cardFacts(page.sections, pages);
  if (!facts.length) return null;
  return (
    <dl className="pt-2 border-t border-line space-y-1" data-testid="entry-card-facts">
      {facts.map((f, j) => (
        <div key={j} className="flex gap-2 text-[11px] min-w-0">
          <dt className="font-code uppercase tracking-wider text-slate-500 shrink-0">{f.key}</dt>
          <dd className="text-slate-300 truncate">{f.value}</dd>
        </div>
      ))}
    </dl>
  );
};

export const EntryRow = ({ page, meta, to }) => (
  <Link to={to} data-testid={`entry-row-${page.id}`} data-extinct={extinct(page)}
    className={`flex items-center gap-4 px-4 py-3 hover:bg-veil/[0.03] transition-[background-color,opacity] ${extinct(page) ? "opacity-60" : ""}`}>
    {meta.key === "Ships" ? <div className="w-16 shrink-0"><ShipAvatars page={page} size="w-9 h-9 text-xs" /></div> : <Thumb page={page} meta={meta} className="w-11 h-11 rounded-lg shrink-0 text-base" />}
    <div className="min-w-0 flex-1">
      <p className="font-display text-lg text-slate-100 truncate">{page.title} <OriginBadge page={page} /></p>
      {page.summary && <p className="text-xs text-slate-500 truncate">{page.summary}</p>}
      <div className="flex flex-wrap gap-1.5 mt-1 sm:hidden">
        <RowMeta page={page} meta={meta} />
      </div>
    </div>
    <div className="hidden sm:flex flex-wrap justify-end gap-1.5 max-w-[40%]"><RowMeta page={page} meta={meta} /></div>
    <span className="text-[11px] font-code text-slate-500 shrink-0 w-16 text-right" data-testid="entry-updated">{timeAgo(page.updated_at)}</span>
  </Link>
);

const RowMeta = ({ page, meta }) =>
  meta.key === "Species" ? <SpeciesMeta page={page} className="sm:justify-end" /> :
  meta.key === "Ships" ? <><PairingLine page={page} className="text-xs" /><ShipLabelBadge label={shipLabel(page)} /></> : meta.key === "Events"
    ? page.event_date ? <span className="text-xs font-code text-rose-300">{page.event_date}</span> : null
    : (page.tags || []).slice(0, 4).map((t) => (
      <span key={t} className="text-[11px] font-code text-slate-400 border border-line rounded-full px-2 py-0.5">#{t}</span>
    ));
